// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the one way into MapLibre's private camera transform. MapLibre 6
 * moved it from `map.transform` to `map._camera.transform` (the map now
 * composes a camera instead of being one). It answers questions the public
 * API has no call for: whether a place is round the back of the globe
 * (`isLocationOccluded`), the field of view in radians, the camera's distance
 * to the view centre, and the sphere transform the city close-up corrects
 * (sphereCloseupFix.ts). Read only: nothing here writes to it.
 */

/** The map's transform, or null when the map has none (yet, or in a test double). */
export function mapTransform<T = unknown>(map: unknown): T | null {
  const m = map as { _camera?: { transform?: unknown }; transform?: unknown } | null | undefined
  return ((m?._camera?.transform ?? m?.transform) as T | undefined) ?? null
}

/** Whether a place is hidden round the back of the globe (never on a flat map). */
export function isOccluded(map: unknown, lngLat: unknown): boolean {
  const t = mapTransform<{ isLocationOccluded?: (l: unknown) => boolean }>(map)
  return t?.isLocationOccluded?.(lngLat) ?? false
}

/** What `transformCameraUpdate` takes and gives, the parts used here. */
export type CameraUpdateHook = (next: { center: { lng: number; lat: number }; zoom: number; pitch: number; bearing: number }) => Record<string, unknown>

/** The camera-update hook in place: `setTransformCameraUpdate` sets it since MapLibre 6, which keeps it on `_camera`. */
export function cameraUpdateHook(map: unknown): CameraUpdateHook | null {
  const m = map as { _camera?: { transformCameraUpdate?: CameraUpdateHook | null }; transformCameraUpdate?: CameraUpdateHook | null }
  return m._camera?.transformCameraUpdate ?? m.transformCameraUpdate ?? null
}

/**
 * Put a camera-update hook in place: `setTransformCameraUpdate` since
 * MapLibre 6 (assigning `transformCameraUpdate`, as 5 took it, would change
 * nothing there, silently).
 */
export function setCameraUpdateHook(map: unknown, hook: CameraUpdateHook | null): void {
  const m = map as { setTransformCameraUpdate?: (h: CameraUpdateHook | null) => void; transformCameraUpdate?: CameraUpdateHook | null }
  if (typeof m.setTransformCameraUpdate === 'function') m.setTransformCameraUpdate(hook)
  else m.transformCameraUpdate = hook
}

/**
 * Whether the style itself has loaded, so it takes `setProjection` and
 * layer changes. `isStyleLoaded()` also waits for every source and image,
 * which a new globe still fetching tiles can take a long while to reach.
 */
export function styleReady(map: unknown): boolean {
  return (map as { style?: { _loaded?: boolean } }).style?._loaded === true
}
